import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useMemo,
} from 'react';
import webBiLogger from '@wix/web-bi-logger';
import {
  wixStudioTpPageView,
  wixStudioTpMainAction,
  wixStudioTpGeneralAction,
  wixStudioTpHover,
} from '@wix/bi-logger-generic/v2';
import { useInitialState } from './InitialStateContext';
import { useTranslation } from '@wix/wix-i18n-config';
import { useExperiments } from './ExperimentsContext';
import { useMenuCategoryGroups } from '../hooks/useMenuCategoryGroups';
import { useSearch } from '../hooks/useSearch';
import { wixStudioTpSearchSrc2Evid904 } from '@wix/bi-logger-top-funnel-data/v2';
import { useSelectedCategory } from '../hooks/useSelectedCategory';
import { useTemplatesPageUI } from './TemplatesPageUIContext';
import { usePagination } from '../hooks/usePagination';

type TemplateEventProps = {
  templateId: string;
  slug: string;
  index?: number;
  title?: string;
  buttonVersion?: string;
  tabName?: string;
  categorySlug?: string;
};

type GeneralActionProps = {
  action: string;
  actionValue?: string;
  index?: number;
  templateId?: string;
  slugId?: string;
  categorySlug?: string;
  criteria?: string;
};

type PaginationClickProps = {
  actionValue: string;
};

type SearchParams = {
  criteria: string;
  total?: number;
};

export interface BILoggerContextValue {
  reportTemplatesPageView: () => void;
  reportEditTemplate: (props: TemplateEventProps) => void;
  reportViewTemplate: (props: TemplateEventProps) => void;
  reportBrowseSidebar: (button: string) => void;
  reportPaginationClick: (props: PaginationClickProps) => void;
  reportGeneralAction: (props: GeneralActionProps) => void;
  reportAboveFoldImpression: (props: TemplateEventProps) => void;
  reportSearch: (props: SearchParams) => void;
}

interface BiLoggerProviderProps {}

const BiLoggerContext = createContext<BILoggerContextValue>({
  reportTemplatesPageView: () => {},
  reportEditTemplate: () => {},
  reportViewTemplate: () => {},
  reportBrowseSidebar: () => {},
  reportPaginationClick: () => {},
  reportGeneralAction: () => {},
  reportAboveFoldImpression: () => {},
  reportSearch: () => {},
});

const logger = webBiLogger
  .factory({ endpoint: 'generic' })
  .updateDefaults({ src: 2 })
  .logger();
export const BiLoggerProvider: FC<PropsWithChildren<BiLoggerProviderProps>> = ({
  children,
}) => {
  const initialState = useInitialState();
  const { t } = useTranslation();
  const experiments = useExperiments();
  const categoryGroups = useMenuCategoryGroups();
  const { criteria } = useSearch();
  const selectedCategory = useSelectedCategory();
  const { isTopBarSticky } = useTemplatesPageUI();
  const { currentPage } = usePagination();
  const categorySlug = selectedCategory?.slug;

  const tabName = useMemo(
    () => (criteria ? 'search' : categorySlug),
    [criteria, categorySlug],
  );

  const getCategoryBySlug = useCallback(
    (slug?: string) => {
      return initialState.book?.categories.find((c) => c.slug === slug);
    },
    [initialState.book?.categories],
  );

  const getBucketNameByCategorySlug = useCallback(
    (slug?: string) => {
      const missingCategory = 'MISSING_CATEGORY';
      if (!slug) {
        return missingCategory;
      }

      return (
        categoryGroups.find((group) =>
          group.categories.some((category) => category.slug === slug),
        )?.title || missingCategory
      );
    },
    [experiments, t],
  );

  const getNewStudioTPProperties = useCallback(
    (slug: string | undefined = categorySlug) => {
      const { categoryId, displayName } = getCategoryBySlug(slug) ?? {};
      return {
        browse_type: getBucketNameByCategorySlug(slug),
        browse_value: displayName,
        category_id: categoryId,
        criteria,
      };
    },
    [categorySlug, getBucketNameByCategorySlug, getCategoryBySlug, criteria],
  );

  // evid: 900
  const reportTemplatesPageView = useCallback(() => {
    void logger.report(
      wixStudioTpPageView({
        origin: initialState.referrer,
        tab_name: tabName,
        page_num: currentPage,
        ...getNewStudioTPProperties(),
      }),
    );
  }, [initialState.referrer, tabName, getNewStudioTPProperties]);

  // evid: 901
  const reportEditTemplate = useCallback(
    ({
      templateId,
      title,
      buttonVersion,
      index,
      slug,
      tabName: tabNameOverride,
      categorySlug,
    }: TemplateEventProps) => {
      void logger.report(
        wixStudioTpMainAction({
          action: 'edit',
          template_id: templateId,
          origin: initialState.referrer,
          tab_name: tabNameOverride ?? tabName,
          title,
          template_index: index,
          slug_id: slug,
          button_version: buttonVersion,
          page_num: currentPage,
          ...getNewStudioTPProperties(categorySlug),
        }),
      );
    },
    [initialState.referrer, getNewStudioTPProperties, tabName, currentPage],
  );

  // evid: 901
  const reportViewTemplate = useCallback(
    ({
      templateId,
      title,
      index,
      slug,
      tabName: tabNameOverride,
      categorySlug,
    }: TemplateEventProps) => {
      void logger.report(
        wixStudioTpMainAction({
          action: 'view',
          template_id: templateId,
          origin: initialState.referrer,
          tab_name: tabNameOverride ?? tabName,
          title,
          template_index: index,
          slug_id: slug,
          page_num: currentPage,
          ...getNewStudioTPProperties(categorySlug),
        }),
      );
    },
    [initialState.referrer, tabName, getNewStudioTPProperties, currentPage],
  );

  // evid: 902
  const reportBrowseSidebar = useCallback(
    (button: string) => {
      void logger.report(
        wixStudioTpGeneralAction({
          action: 'sidebar_browse',
          action_value: button,
          is_sticky: isTopBarSticky,
          ...getNewStudioTPProperties(),
        }),
      );
    },
    [criteria, getNewStudioTPProperties, isTopBarSticky],
  );

  // evid: 902
  const reportGeneralAction = useCallback(
    ({
      action,
      actionValue,
      index,
      templateId,
      slugId,
      categorySlug: categorySlugOverride,
      criteria: criteriaOverride,
    }: GeneralActionProps) => {
      void logger.report(
        wixStudioTpGeneralAction({
          action,
          action_value: actionValue,
          template_id: templateId,
          slug_id: slugId,
          template_index: index,
          is_sticky: isTopBarSticky,
          ...getNewStudioTPProperties(categorySlugOverride),
          criteria: criteriaOverride ?? criteria,
        }),
      );
    },
    [criteria, getNewStudioTPProperties, isTopBarSticky],
  );

  // evid: 902
  const reportPaginationClick = useCallback(
    ({ actionValue }: PaginationClickProps) => {
      void logger.report(
        wixStudioTpGeneralAction({
          action: 'pagination_button_click',
          action_value: actionValue,
          is_sticky: isTopBarSticky,
          page_num: currentPage,
          ...getNewStudioTPProperties(),
        }),
      );
    },
    [criteria, getNewStudioTPProperties, isTopBarSticky, currentPage],
  );

  // evid: 903
  const reportAboveFoldImpression = useCallback(
    ({
      slug,
      title,
      index,
      templateId,
      tabName: tabNameOverride,
      categorySlug,
    }: TemplateEventProps) => {
      void logger.report(
        wixStudioTpHover({
          origin: initialState.referrer,
          slug_id: slug,
          tab_name: tabNameOverride ?? tabName,
          template_id: templateId,
          template_index: index,
          title,
          window_height: window.innerHeight,
          window_width: window.innerWidth,
          ...getNewStudioTPProperties(categorySlug),
        }),
      );
    },
    [tabName, criteria, getNewStudioTPProperties, initialState.referrer],
  );

  // evid: 904
  const reportSearch = useCallback(
    ({ criteria, total }: SearchParams) => {
      void logger.report(
        wixStudioTpSearchSrc2Evid904({
          criteria,
          numberOfTemplates: total,
          is_sticky: isTopBarSticky,
        }),
      );
    },
    [isTopBarSticky],
  );

  return (
    <BiLoggerContext.Provider
      value={{
        reportTemplatesPageView,
        reportEditTemplate,
        reportViewTemplate,
        reportBrowseSidebar,
        reportGeneralAction,
        reportPaginationClick,
        reportAboveFoldImpression,
        reportSearch,
      }}
    >
      {children}
    </BiLoggerContext.Provider>
  );
};

export const useBiLogger = () => {
  const context = React.useContext(BiLoggerContext);
  if (!context) {
    throw new Error('useBiLogger must be used within a BiLoggerProvider');
  }

  return context;
};
